// ./client/src/hooks/useSmsTask.js
import { useState, useRef, useEffect, useCallback } from "react";
import { useAppContext } from "../context/appContext";

// RingCentral's exact status values — do not change casing
const TERMINAL_STATUSES = ["Delivered", "Sent", "Failed"];
const POLL_INTERVAL_MS = 2000;

// stored phones are "713-555-1234"; RingCentral wants E.164
const normalizePhone = (phone) => {
  const digits = (phone || "").replace(/\D/g, "");
  if (!digits) return "";
  if (digits.length === 10) return `+1${digits}`;
  return `+${digits}`;
};

const useSmsTask = () => {
  const { user } = useAppContext();
  const [status, setStatus] = useState("idle"); // idle | Queued | Sending | Delivered | Sent | Failed
  const [error, setError] = useState(null);
  const pollRef = useRef(null);

  // stop polling if the component unmounts mid-send
  useEffect(() => () => clearInterval(pollRef.current), []);

  const authHeaders = useCallback(() => {
    const headers = {
      "Content-Type": "application/json",
      Mode: `${user?.user_mode}`,
    };
    const token = localStorage.getItem("token");
    if (token) headers["Authorization"] = `Bearer ${token}`;
    return headers;
  }, [user]);

  const startPolling = useCallback((taskId, headers) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const res = await fetch(`/api/v1/messages/task-status/${taskId}`, { headers });
        const data = await res.json();
        setStatus(data.status);
        if (TERMINAL_STATUSES.includes(data.status)) {
          clearInterval(pollRef.current);
          setError(data.error || null);
        }
      } catch {
        clearInterval(pollRef.current);
        setStatus("Failed");
        setError("Lost connection to server");
      }
    }, POLL_INTERVAL_MS);
  }, []);

  const handleResponse = useCallback(
    async (res, headers) => {
      const data = await res.json();
      if (res.status === 202) {
        startPolling(data.taskId, headers);
      } else if (data.msg === "Message already sent") {
        setStatus("Delivered");
      } else {
        setStatus("Failed");
        setError(data.msg || "Failed to start send");
      }
    },
    [startPolling]
  );

  // general send: any text to any number
  const sendSms = useCallback(
    async (body) => {
      setStatus("Queued");
      setError(null);
      try {
        const headers = authHeaders();
        const normalizedBody = {
          ...body,
          customerPhone: normalizePhone(body.customerPhone),
        };
        const res = await fetch("/api/v1/messages/send", {
          method: "POST",
          headers,
          body: JSON.stringify(normalizedBody),
        });
        await handleResponse(res, headers);
      } catch {
        setStatus("Failed");
        setError("Network error");
      }
    },
    [authHeaders, handleResponse]
  );

  const reset = useCallback(() => {
    clearInterval(pollRef.current);
    setStatus("idle");
    setError(null);
  }, []);

  return { status, error, sendSms, reset };
};

export default useSmsTask;